import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import PromissoryNoteService from '../../services/PromissoryNoteService';

export default function OwnPromissoryNoteListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [newNote, setNewNote] = useState({
        amount: 0,
        dueDate: null,
        drawer: 'Firmamız',
        guarantor: '',
        description: '',
        type: 2 // Outbound
    });
    const toast = useRef(null);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const res = await PromissoryNoteService.getPromissoryNotes(null, 2); // Only Outbound
            setNotes(res.data || []);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Senetler yüklenirken hata oluştu' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    const saveNote = async () => {
        if (!newNote.amount || !newNote.dueDate || !newNote.drawer) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen gerekli alanları doldurun' });
            return;
        }

        try {
            const res = await PromissoryNoteService.createPromissoryNote(newNote);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Senet tanımlandı' });
                setShowDialog(false);
                fetchNotes();
                setNewNote({
                    amount: 0,
                    dueDate: null,
                    drawer: 'Firmamız',
                    guarantor: '',
                    description: '',
                    type: 2
                });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Kaydedilirken hata oluştu' });
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statuses = {
            1: { label: 'Portföyde', severity: 'info' },
            2: { label: 'Verildi', severity: 'warning' },
            3: { label: 'Tahsil Edildi', severity: 'success' },
            4: { label: 'Ödendi', severity: 'success' },
            5: { label: 'İade Edildi', severity: 'danger' }
        };
        const status = statuses[rowData.status] || { label: 'Bilinmiyor', severity: 'secondary' };
        return <Tag value={status.label} severity={status.severity} />;
    };

    const renderHeader = () => {
        return (
            <div className="flex justify-content-between align-items-center">
                <Button label="Yeni Senet Tanımla" icon="pi pi-plus" className="p-button-sm !rounded-xl" onClick={() => setShowDialog(true)} />
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Ara..."
                        className="p-inputtext-sm !py-2.5 !rounded-xl"
                    />
                </span>
            </div>
        );
    };

    return (
        <div className="p-4 space-y-4 animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="!bg-orange-600 p-4 rounded-xl shadow-lg shadow-orange-600/20">
                            <i className="pi pi-file text-white text-xl"></i>
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-800 m-0">Kendi Senetlerim</h1>
                            <p className="text-xs text-slate-500 font-medium">Firmamıza ait tanımlanmış senetler</p>
                        </div>
                    </div>
                </div>

                <DataTable value={notes} loading={loading} paginator rows={10} globalFilter={globalFilter} header={renderHeader()}
                    responsiveLayout="scroll" className="p-datatable-sm mt-2">
                    <Column field="issueDate" header="Tanımlama Tarihi" body={(row) => new Date(row.issueDate).toLocaleDateString('tr-TR')} sortable />
                    <Column field="dueDate" header="Vade" body={(row) => new Date(row.dueDate).toLocaleDateString('tr-TR')} sortable />
                    <Column field="drawer" header="Borçlu" sortable />
                    <Column field="supplierName" header="Verilen Tedarikçi" sortable body={(row) => row.supplierName || <span className="text-slate-400 font-normal">Portföyde</span>} />
                    <Column field="amount" header="Tutar" body={(row) => `₺${row.amount.toLocaleString('tr-TR')}`} sortable />
                    <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                </DataTable>
            </div>

            <Dialog header="Yeni Senet Tanımla" visible={showDialog} style={{ width: '450px' }} modal onHide={() => setShowDialog(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="İptal" icon="pi pi-times" onClick={() => setShowDialog(false)} className="p-button-text" />
                        <Button label="Kaydet" icon="pi pi-check" onClick={saveNote} className="p-button-primary !rounded-xl" />
                    </div>
                }>
                <div className="flex flex-col gap-4 mt-2">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Borçlu</label>
                        <InputText value={newNote.drawer} onChange={(e) => setNewNote({ ...newNote, drawer: e.target.value })} className="!py-2.5 !rounded-xl" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Kefil</label>
                        <InputText value={newNote.guarantor} onChange={(e) => setNewNote({ ...newNote, guarantor: e.target.value })} className="!py-2.5 !rounded-xl" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Vade Tarihi</label>
                        <Calendar value={newNote.dueDate} onChange={(e) => setNewNote({ ...newNote, dueDate: e.value })} dateFormat="dd.mm.yy" showIcon className="!rounded-xl" appendTo={document.body} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Tutar</label>
                        <InputNumber value={newNote.amount} onValueChange={(e) => setNewNote({ ...newNote, amount: e.value })} mode="currency" currency="TRY" locale="tr-TR" className="!rounded-xl" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Açıklama</label>
                        <InputText value={newNote.description} onChange={(e) => setNewNote({ ...newNote, description: e.target.value })} className="!py-2.5 !rounded-xl" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
